/* Vue & Helpers */
import store from '@/store/store'
import { useRoute } from 'vue-router'
import { menuIconThirdLevelMapper, menuIconMapper } from '@/assets/scripts/mappers/menu'

/**
* Check if visually this menu entry is terminal
@menuEntry {Object} menu entry with children info
@return {Boolean}
*/
export const checkIfVisuallyTerminal = menuEntry => {
	if (menuEntry.terminal) return menuEntry.terminal

	const children = menuEntry.children
	if (children?.length) {
		const childrenWithNoType = children.filter(obj => !obj.additionalData || !obj.additionalData.menuType)

		return !childrenWithNoType.length
	}
}

/**
* Check if when clicking the entry will open a feature with tabs, if so, we will want to redirect to first tab entry instead of "true" route
@menuEntry {Object} menu entry with children info
@return {String}
*/
export const parseMenuEntryLink = menuEntry => {
	if (menuEntry.terminal) return false

	const children = menuEntry.children
	if (children?.length) {
		const childrenTabs = children.filter(obj => obj.additionalData?.menuType === 'TAB')

		return !childrenTabs.length ? false : childrenTabs[0].link
	}
}

/** Parsing our menu to add icons for each entry, firstTabLink, and fix terminal variable
@menuItems { array }
@return { array }
*/
export const structureMenuParser = (menuItems) => {
	const parseEntry = entry => {
		return {
			...entry,
			terminal: checkIfVisuallyTerminal(entry),
			link: entry.link,
			firstTabLink: parseMenuEntryLink(entry),
			icon: menuIconMapper(entry.label) || menuIconThirdLevelMapper(entry.label),
			childrenAreCardMenuType: entry.children ? !!entry.children.find(obj => obj.additionalData?.menuType === 'CARD') : false,
			children: entry.children ? entry.children.map(childrenEntry => {
				return { ...parseEntry(childrenEntry) }
			}) : null
		}
	}

    return menuItems.map(firstLevel => {
		return { ...parseEntry(firstLevel) }
    })
}

/** Parsing our menu to only return the third level children of menu entry based on route
@currentRoute {String} - current route
@menuItems { array }
@return { array }
*/
export const thirdLevelMenuParser = (currentRoute, allMenuEntries) => {
	if (!allMenuEntries) return

    const firstLevelMenu = allMenuEntries.find(menu => currentRoute.includes(menu.link))
    const thirdLevelCards = []

    /* This validation is only to prevent errors if mdw for some reason disable all children */
	if (firstLevelMenu.children && firstLevelMenu.children.length) {
		const secondLevelMenu = firstLevelMenu.children.find((menu) => { return currentRoute.includes(menu.link) })
        /* This validation is only to prevent errors if mdw for some reason disable all children */
		if (secondLevelMenu && secondLevelMenu.children && secondLevelMenu.children.length) {
			if (secondLevelMenu.link === currentRoute) {
				secondLevelMenu.children.forEach(menu => {
					thirdLevelCards.push({
						...menu,
						description: menu.additionalData ? menu.additionalData.description : ''
					})
				})
			}

			/* Checking if we have third level with CARD type children */
			const thirdLevelMenu = secondLevelMenu.children.find((menu) => { return currentRoute.includes(menu.link) })

			if (thirdLevelMenu && thirdLevelMenu.children && thirdLevelMenu.children.length) {
				if (thirdLevelMenu.link === currentRoute) {
					thirdLevelMenu.children.forEach(menu => {
						thirdLevelCards.push({
							...menu,
							description: menu.additionalData ? menu.additionalData.description : ''
						})
					})
				}
			}
		}
	}

	return thirdLevelCards
}

/** Parsing our menu to only return the TABS TYPE of menu entry based on route
@currentRoute {String} - current route
@menuItems { array }
@return { array }
*/
export const getCurrentMenuEntryTabs = (allMenuEntries, currentRoute) => {
	if (!allMenuEntries) return

    const firstLevelMenu = allMenuEntries.find(menu => currentRoute.includes(menu.link))
    const tabs = []

    /* This validation is only to prevent errors if mdw for some reason disable all children */
	if (firstLevelMenu && firstLevelMenu.children?.length) {
		const children = firstLevelMenu.children
		const secondLevelTabs = children.filter(obj => obj.additionalData?.menuType === 'TAB')
		const isOneOfThirdSecondTabs = !!secondLevelTabs.find(menu => menu.link === currentRoute)
		/* We need to check if our currentRoute is one of the tabs in array */
		if (secondLevelTabs.length && isOneOfThirdSecondTabs) secondLevelTabs.forEach(menu => tabs.push(menu))
		else {
			const secondLevelMenu = children.find((menu) => { return currentRoute.includes(menu.link) })

			if (secondLevelMenu) {
				const thirdLevelTabs = secondLevelMenu.children.filter(obj => obj.additionalData?.menuType === 'TAB')
				const isOneOfThirdLevelTabs = !!thirdLevelTabs.find(menu => menu.link === currentRoute)
				/* We need to check if our currentRoute is one of the tabs in array */
				if (thirdLevelTabs.length && isOneOfThirdLevelTabs) thirdLevelTabs.forEach(menu => tabs.push(menu))
				else {
					const thirdLevelMenu = secondLevelMenu.children.find((menu) => { return currentRoute.includes(menu.link) })

					if (thirdLevelMenu) {
						const forthLevelTabs = thirdLevelMenu.children.filter(obj => obj.additionalData?.menuType === 'TAB')
						const isOneOfForthLevelTabs = !!forthLevelTabs.find(menu => menu.link === currentRoute)
						/* We need to check if our currentRoute is one of the tabs in array */
						if (forthLevelTabs.length && isOneOfForthLevelTabs) forthLevelTabs.forEach(menu => tabs.push(menu))
					}
				}
			}
		}
	}

	return tabs
}

/** Parsing our menu to only return the BUTTON TYPE of menu entry based on route
@currentRoute {String} - current route
@return { array }
*/
export const getCurrentMenuEntryTopButtons = (currentRoute) => {
	const menuItems = store.getters['menu/getMenu']
	if (!menuItems) return

	let buttonsTemp

	const findCurrentMenuItem = (entry) => {
		/* If route match, we will return the menuEntry */
		if (entry.link === currentRoute) return true
		/* Else we will check their children */
		else if (currentRoute.includes(entry.link) && entry.children.length) {
			let temp
			entry.children.forEach(secondMenu => {
				if (findCurrentMenuItem(secondMenu)) {
					/* If our current menu is a tab, we need to check his siblings */
					if (secondMenu.additionalData?.menuType === 'TAB') {
						buttonsTemp = entry.children.filter(obj => obj.additionalData?.menuType === 'BUTTON' && obj.link !== currentRoute)
					} else {
						buttonsTemp = secondMenu.children.filter(obj => obj.additionalData?.menuType === 'BUTTON' && obj.link !== currentRoute)
					}
				}
			})
		}
	}

	menuItems.forEach(menu => {
		findCurrentMenuItem(menu)
	})

	return buttonsTemp
}

/** Creating an object based on our menu, with info from our currentRoute path
@menuItemId { Number } - menu item Id
@return { object }
*/
export const getMenuItemData = (menuItemId) => {
	const menuItems = store.getters['menu/getMenu']
	if (!menuItems) return null

	if (menuItems.find(obj => obj.id === menuItemId)) {
		return menuItems.find(obj => obj.id === menuItemId)
	} else {
		let temp
		menuItems.forEach(item => {
			if (item.children) {
                if (item.children.find(obj => obj.id === menuItemId)) {
                    temp = item.children.find(obj => obj.id === menuItemId)
                } else {
                    item.children.forEach(subItem => {
                        if (subItem.children && subItem.children.find(obj => obj.id === menuItemId)) {
                            temp = subItem.children.find(obj => obj.id === menuItemId)
                        }
                    })
                }
			}
		})
		return temp
	}
}

/** Creating an object based on current route and menuItens
@customRoute {String} - for some exceptions we will want to send a route to get menu info of it, mas used it in another feature
@return { object } - all menu item info
*/
export const getCurrentRouteMenuItemInfo = (customRoute) => {
	const menuItems = store.getters['menu/getMenu']
	const currentRoute = customRoute || useRoute().fullPath

	if (!menuItems || !currentRoute) return null

	if (menuItems.find(obj => currentRoute.includes(obj.link))) {
		const firstLevel = menuItems.find(obj => currentRoute.includes(obj.link))

		if (firstLevel.link === currentRoute) return firstLevel
		/* Checking firstLevel children */
		else if (firstLevel.children?.find(obj => currentRoute.includes(obj.link))) {
			const secondLevel = firstLevel.children.find(obj => currentRoute.includes(obj.link))

			if (secondLevel.link === currentRoute) return secondLevel
			else if (secondLevel.children?.find(obj => currentRoute.includes(obj.link))) {
				const thirdLevel = secondLevel.children.find(obj => currentRoute.includes(obj.link))

				if (thirdLevel.link === currentRoute) return thirdLevel
				return null
			}
		}
	}
}

/** Creating an object based on current route and menuItens
@return { object } - Additional Data for that route, provided by mdw menu api
*/
export const getCurrentRouteMenuAdditionalData = () => {
	const menuItems = store.getters['menu/getMenu']
	const currentRoute = useRoute().fullPath

	if (!menuItems || !currentRoute) return null

	const firstLevelMenu = menuItems.find(obj => currentRoute.includes(obj.link))
	let additionalData

	const findCurrentMenuAdditionalData = (entry) => {
		/* If route match, we will return the menuEntry */
		if (entry.link === currentRoute) additionalData = entry.additionalData
		/* Else we will check their children */
		else if (currentRoute.includes(entry.link) && entry.children.length) {
			entry.children.forEach(deepEntry => {
				findCurrentMenuAdditionalData(deepEntry)
			})
		}
	}

	if (firstLevelMenu) findCurrentMenuAdditionalData(firstLevelMenu)

	return additionalData
}

/** Creating an object based on our menu, with info from our currentRoute path
@menuItems { Array } - all menu itens
@operatorsList { Array } - list of payments operators list
@return { Array }
*/
export const parseMenuForHeaderSearch = (menuItems, operatorsList) => {
	return menuItems.reduce((acc, menuItem) => {
        /* If first level and terminal */
        if (menuItem.terminal || !menuItem.children.length) {
            acc.push(menuItem)
            return acc
        }

		/* Finding if route in second level is mapped */
        if (menuItem.children && !!menuItem.children.length) {
			menuItem.children.forEach(secondLevel => {
				/* Payments Logic */
				if (secondLevel.label === 'payments_services' && operatorsList) {
					operatorsList.forEach(operatorGroup => {
						if (operatorGroup.children && !!operatorGroup.children.length) {
							operatorGroup.children.forEach(operatorType => {
								if (operatorType.children && !!operatorType.children.length) {
									operatorType.children.forEach(operator => {
										acc.push({
											id: operator.id,
											name: `${menuItem.name} / ${secondLevel.name} / ${operatorGroup.name} / ${operatorType.name} / ${operator.name}`,
											link: secondLevel.link,
											label: menuItem.label,
											paymentOperator: operator
										})
									})
									return
								}

								acc.push({
									id: operatorType.id,
									name: `${menuItem.name} / ${secondLevel.name} / ${operatorGroup.name} / ${operatorType.name}`,
									link: secondLevel.link,
									label: menuItem.label,
									paymentOperator: operatorType
								})
							})
							return
						}

						acc.push({
							id: operatorGroup.id,
							name: `${menuItem.name} / ${secondLevel.name} / ${operatorGroup.name}`,
							link: secondLevel.link,
							label: menuItem.label,
							paymentOperator: operatorGroup
						})
					})
					return
				}
				acc.push({
					...secondLevel,
					name: `${menuItem.name} / ${secondLevel.name}`,
					label: menuItem.label === 'userArea' ? secondLevel.label : menuItem.label
				})
			})
		}

		return acc
	}, [])
}

/** Creating an object based on our menu, with info from our currentRoute path
@menuItems { Array } - all menu itens
@currentRoute {String} - current route path
@return { Object }
*/
export const breadcrumsGenerator = (menuItems, viewRoute) => {
	// let isEntryWithTabs = false

	const breadcrums = {
		currentArea: '',
		parentMenus: []
	}

	if (!menuItems) return breadcrums

	const routeFirstLevel = `/${viewRoute.split('/')[1]}` // every route starts with "/", so whe first level property is the [1]
	const firstLevelMenu = menuItems.find((menu) => {	return routeFirstLevel.includes(menu.link) })

	/* First we will check if this menu has second levels */
	if (firstLevelMenu && !firstLevelMenu.terminal && firstLevelMenu.children?.length) {
		/* We know that if we are a chidren, we will want to add the parents to our array */
		breadcrums.parentMenus.push({ text: firstLevelMenu.name })

		const secondLevelMenu = firstLevelMenu.children.find((menu) => { return viewRoute.includes(menu.link) })

		/* Checking if secondLevelMenu Exists */
		if (!secondLevelMenu) {
			breadcrums.currentArea = firstLevelMenu.name
		/*
		* Checking if second menu has children (card pickers)
		* and if we are not on third level already
		*/
		} else if (secondLevelMenu.children.length && secondLevelMenu.link !== viewRoute) {
			const thirdLevelMenu = secondLevelMenu.children.find((menu) => { return viewRoute.includes(menu.link) })

			/* If my current entry is a tab, we need to place our parent as currentArea instead */
			if (thirdLevelMenu?.additionalData?.menuType === 'TAB') {
				breadcrums.currentArea = secondLevelMenu.name
			/* Checking if forthLevelMenu Exists */
			} else if (thirdLevelMenu?.children.length && thirdLevelMenu.link !== viewRoute) {
				const forthLevelMenu = thirdLevelMenu.children.find((menu) => { return viewRoute.includes(menu.link) })

				breadcrums.currentArea = forthLevelMenu ? forthLevelMenu.name : '-'
				breadcrums.parentMenus.push({ text: secondLevelMenu.name, link: secondLevelMenu.firstTabLink ?? secondLevelMenu.link })
				breadcrums.parentMenus.push({ text: thirdLevelMenu.name, link: thirdLevelMenu.link })
			} else {
				breadcrums.currentArea = thirdLevelMenu ? thirdLevelMenu.name : '-'
				breadcrums.parentMenus.push({ text: secondLevelMenu.name, link: secondLevelMenu.link })
			}
		} else {
			breadcrums.currentArea = secondLevelMenu.name
		}
	} else if (firstLevelMenu) {
		breadcrums.currentArea = firstLevelMenu.name
	}
	return breadcrums
}

/** Creating an object based on our menu, with info from our currentRoute path
@return { Object }
*/
export const getInitialEntry = () => {
	const menuItems = store.getters['menu/getMenu']
	let initial = null

    for (let index = 0; index < menuItems.length; index++) {
        if (menuItems[index].initial) {
            initial = menuItems[index]
            break
        } else if (menuItems[index].children?.length) {
            const secondLevel = menuItems[index].children
            for (let secondIndex = 0; secondIndex < secondLevel.length; secondIndex++) {
                if (secondLevel[secondIndex].initial) {
                    initial = secondLevel[secondIndex]
                    break
                }
            }
        }
    }

	return initial
}
